import React from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';

/**
 * ListShell — canonical chrome for every list page (LAT-09).
 *
 * Layout (one line):
 *   [search input — left, grows]   [filter chips — right]
 *
 *   <list body>
 *
 * Rules:
 * - NO KPI cards on list pages. Move dashboards to /dashboard.
 * - NO "Save Filter". One row of search + chips. Period.
 * - Selected chip uses ONE selected-chip token (.list-chip--on).
 * - Active filters are exposed as dismissible chips. Clicking a chip with
 *   `onRemove` set acts as an "x" (whole chip is the removal target).
 *
 * Usage:
 *   <ListShell
 *     search={search}
 *     onSearchChange={setSearch}
 *     placeholder="Search customers…"
 *     chips={[
 *       { label: 'All', active: filter==='all', onClick: ()=>setFilter('all') },
 *       { label: 'Active', active: filter==='active', onClick: ()=>setFilter('active') },
 *       { label: 'Lost', active: filter==='lost', onClick: ()=>setFilter('lost') },
 *     ]}
 *   >
 *     <Table … />
 *   </ListShell>
 */
export default function ListShell({
  search = '',
  onSearchChange,
  placeholder = 'Search…',
  chips = [],
  rightSlot,           // optional extra controls (sort, density, etc.) at far right
  children,
  className,
  toolbarClassName,
}) {
  return (
    <div className={clsx('flex flex-col gap-4', className)}>
      <div
        className={clsx(
          'flex items-center gap-3 flex-wrap',
          toolbarClassName
        )}
      >
        {/* Search, left, grows */}
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <MagnifyingGlassIcon
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4"
            style={{ color: 'var(--color-text-muted)' }}
            aria-hidden="true"
          />
          {/*
            IDI-165: type="text", not type="search". WebKit renders its own
            `::-webkit-search-cancel-button` for `type="search"`, which
            renders alongside our custom clear button below and produces two
            X controls side-by-side (one native, one custom). The native
            button cannot be styled and carries no accessible name; the
            custom one is tokenised, styled to the design system, and has
            `aria-label="Clear search"`. Keep type="text".
          */}
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange?.(e.target.value)}
            placeholder={placeholder}
            className="w-full pl-9 pr-9 py-2 text-sm focus:outline-none focus-visible:ring-2"
            style={{
              height: 'var(--input-h)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)',
              backgroundColor: 'var(--color-surface)',
              color: 'var(--color-text-default)',
            }}
            aria-label={placeholder}
          />
          {search && (
            <button
              type="button"
              onClick={() => onSearchChange?.('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-[color:var(--neutral-100)]"
              aria-label="Clear search"
            >
              <XMarkIcon className="h-4 w-4" style={{ color: 'var(--color-text-muted)' }} />
            </button>
          )}
        </div>

        {/* Filter chips, right */}
        {chips.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap" role="group" aria-label="Filters">
            {chips.map((c, i) => (
              <FilterChip key={c.key || c.label || i} {...c} />
            ))}
          </div>
        )}

        {rightSlot && <div className="ml-auto flex items-center gap-2">{rightSlot}</div>}
      </div>

      {children}
    </div>
  );
}

export function FilterChip({ label, active, count, onClick, onRemove, className }) {
  const isRemovable = typeof onRemove === 'function';
  return (
    <button
      type="button"
      onClick={isRemovable ? onRemove : onClick}
      className={clsx(
        'inline-flex items-center gap-1.5 px-3 text-xs font-medium border transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-action-primary)] focus-visible:ring-offset-1',
        className
      )}
      style={{
        borderRadius: 'var(--radius-full)',
        height: 'var(--input-h)',
        backgroundColor: active
          ? 'var(--red-50)'
          : 'var(--color-surface)',
        color: active ? 'var(--red-700)' : 'var(--color-text-default)',
        borderColor: active
          ? 'var(--red-200)'
          : 'var(--color-border)',
      }}
      aria-pressed={!!active}
    >
      <span>{label}</span>
      {typeof count === 'number' && (
        <span
          className="px-1.5 rounded-full text-[10px] font-semibold"
          style={{
            backgroundColor: active
              ? 'color-mix(in srgb, var(--red-700) 12%, transparent)'
              : 'var(--neutral-100)',
            color: active ? 'var(--red-700)' : 'var(--color-text-muted)',
          }}
        >
          {count}
        </span>
      )}
      {isRemovable && (
        <XMarkIcon className="h-3 w-3" aria-hidden="true" />
      )}
    </button>
  );
}
